import {
    getApiErrorMessage,
    IApiAxiosError,
    ILibraryDocument,
    ILibraryDocumentListItem,
    updateLibraryDocument,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface RenameDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    document: ILibraryDocumentListItem | ILibraryDocument;
    onRenamed: () => void;
}

export const RenameDocumentDialog = ({
    open,
    onClose,
    document: doc,
    onRenamed,
}: RenameDocumentDialogProps) => {
    const queryClient = useQueryClient();
    const [name, setName] = useState(doc.document_name);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setName(doc.document_name);
    }, [doc.document_name]);

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            updateLibraryDocument(doc.id, { document_name: name.trim() }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });
            queryClient.invalidateQueries({
                queryKey: ["library-documents"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-document-related", doc.id],
            });
            toast.success("Document renamed successfully");
            onRenamed();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to rename document"));
        },
    });

    const handleSubmit = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Name cannot be empty");
            return;
        }
        if (trimmed.length > 255) {
            setError("Name must be 255 characters or fewer");
            return;
        }
        if (trimmed === doc.document_name) {
            onClose();
            return;
        }
        setError(null);
        mutate();
    };

    const handleClose = () => {
        setName(doc.document_name);
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Rename Document</DialogTitle>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    className="space-y-3"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="rename-document-name">Name</Label>
                        <Input
                            id="rename-document-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onFocus={(e) => e.currentTarget.select()}
                            maxLength={255}
                        />
                    </div>
                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending || !name}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
